/**
 * Pure invoice helpers.
 *
 * Kept out of `InvoiceBits.tsx` so that file only exports components and fast
 * refresh keeps working — the same split as `catalog/form-utils.ts`.
 */
import type { Invoice, InvoiceProduct, InvoiceStatus, MasterRef } from '@/lib/types';

/**
 * True when an invoice carries imported totals that adding a line would destroy.
 *
 * The seven Zoho imports have real money on the header and no line items at
 * all, because the export had no product columns. The moment a first line is
 * posted, the server rewrites all four header totals from that one line, in the
 * same transaction — no confirmation, no 409, no undo, and the original figures
 * are not kept anywhere. This is the test that lets the UI say so first.
 */
export function isLegacyImported(invoice: Invoice, lines: InvoiceProduct[]): boolean {
  return lines.length === 0 && (invoice.subTotal !== '0.00' || invoice.grandTotal !== '0.00');
}

/**
 * Today as 'YYYY-MM-DD' built from LOCAL parts.
 *
 * `toISOString().slice(0, 10)` is the same off-by-one bug as parsing a date
 * string, just mirrored: it would call an invoice overdue a day early east of
 * Greenwich.
 */
export function todayIsoDate(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/**
 * Case-fold and collapse whitespace, so every name comparison in the invoice
 * surface normalises identically.
 *
 * Exported because `InvoiceBits.tsx` compares the same names for tone and two
 * subtly different normalisers would put a badge and its overdue marker on
 * opposite sides of the same row.
 */
export function normaliseStatusName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * The status names that mean "no longer chase this invoice".
 *
 * ⚠️ **THIS IS NOW THE FALLBACK, NOT THE DECISION.** Migration 080 added
 * `is_settled` to `f_invoice_statuses`, so a status row finally says what it
 * MEANS; `isStatusSettled()` reads that flag and only reaches this set when the
 * flag is absent — a pre-080 server, a rollback, or a caller with no master
 * list to hand. It has to stay: the flag being missing must not be read as
 * "not settled", because that direction is the one that produces noise, and
 * silently switching to noise is still a regression.
 *
 * ⚠️ THE SET ITSELF GOES STALE THE DAY AN ADMIN ADDS A STATUS, and nothing will
 * say so. That is exactly why it is no longer the primary test. These are the
 * four rows migration 017 seeded: Draft, Awaiting Payment, Paid, Cancelled.
 * `canceled` is here as the US spelling of a seeded row, not as a guess at a
 * status that might exist.
 *
 * Matched EXACTLY (after case-folding and whitespace collapse), never by
 * containment. Containment is what this replaced, and it was wrong in the one
 * direction that costs money — see `isOverdue`. **Do not reintroduce
 * `includes()` here or anywhere downstream of it**, flag or no flag: the flag
 * removes the need for the guess, it does not make the guess safe.
 *
 * Exported so `InvoiceBits.tsx` derives its settled tones from ONE vocabulary
 * rather than keeping a second copy that has to be edited in step.
 */
export const SETTLED_STATUS_NAMES: ReadonlySet<string> = new Set([
  'paid',
  'cancelled',
  'canceled',
]);

/**
 * Whether a status means the money is no longer owed — **flag first, name only
 * as a fallback.**
 *
 * ⚠️ **THE FLAG IS NOT ON THE DOCUMENT.** An invoice's own `status` is a bare
 * `MasterRef` (`{id, name}`) produced by the server's `toLookup()`, so it
 * carries no `isSettled` and never has. The flag lives on the
 * `GET /invoice-statuses` row, which is why this takes the master list as a
 * second argument and resolves by id. A caller with no list still gets a
 * correct-by-vocabulary answer from the name set.
 *
 * The record is checked for its own flag first anyway. That costs nothing and
 * means the day the backend embeds `isSettled` on the document, this starts
 * reading it with no change here — rather than silently preferring the weaker
 * signal for however long it takes someone to notice.
 *
 * ⚠️ **THE ASYMMETRY IS THE WHOLE POINT, and every unknown resolves the same
 * way.** Not found in the list, no flag, no name match, no status at all — all
 * of it returns `false`, meaning UNSETTLED, meaning the invoice stays
 * chaseable:
 *
 *   - an invoice wrongly shown as OVERDUE is a false alarm; someone opens it,
 *     sees it is fine, and dismisses it;
 *   - an invoice wrongly shown as SETTLED is money nobody chases, on the one
 *     surface finance reads to chase it.
 *
 * Noise is visible and gets corrected. Silence is not. So there is deliberately
 * no branch anywhere in here that can answer "settled" from a guess.
 */
export function isStatusSettled(
  status: MasterRef | null,
  statuses?: readonly InvoiceStatus[],
): boolean {
  if (!status) return false;

  /*
   * A flag on the record itself, if one is ever served there. `typeof` rather
   * than a truthiness check: `false` is a real answer and must be returned as
   * one, not fall through to the name set, which could then disagree with the
   * server about a row the server has explicitly classified.
   */
  const embedded = (status as Partial<InvoiceStatus>).isSettled;
  if (typeof embedded === 'boolean') return embedded;

  const row = statuses?.find((candidate) => candidate.id === status.id);
  if (row && typeof row.isSettled === 'boolean') return row.isSettled;

  return SETTLED_STATUS_NAMES.has(normaliseStatusName(status.name));
}

/**
 * True when an invoice is past its due date and still owed.
 *
 * Overdue itself is a pure string comparison — ISO dates sort
 * lexicographically. The judgement is entirely in deciding what "still owed"
 * means, and that cannot be done correctly from the client.
 *
 * ✅ **THIS IS AUTHORITATIVE NOW, WHEN IT IS GIVEN THE MASTER LIST.** Each row
 * carries a server-set `is_settled` flag, so passing `statuses` here makes the
 * judgement the server's rather than a vocabulary guess. Pass it
 * wherever the caller already has it — every call site that does not is
 * running on the fallback, which still works and is still the weaker answer.
 *
 * The residual name matching and the reason it survives are documented on
 * `isStatusSettled()`, along with the asymmetry that decides which way every
 * unknown falls. The short version, unchanged by the flag: **an unknown is
 * UNSETTLED, so the badge shows.** Noise is visible and gets corrected; silence
 * is not.
 *
 * ⚠️ **Do not implement the fallback as `name.toLowerCase().includes('paid')`.**
 * "Unpaid" contains "paid". So does "Partially Paid" — both of which are the
 * most obvious statuses an admin would ever add, and both would silently
 * switch overdue reporting off for every invoice carrying them. `'cancel'` has
 * the same hole ("Cancellation Requested"). The seeded row is
 * "Awaiting Payment", which is exactly why a substring test can look correct
 * on day one and stop being correct on day two.
 *
 * Same discipline as `stageTone()` in `CompanyDetailPage.tsx`, which guards the
 * identical containment trap ("Past Client" also contains "client") — except
 * that one only picks a badge colour, and this one decides whether an unpaid
 * invoice is reported at all.
 */
export function isOverdue(invoice: Invoice, statuses?: readonly InvoiceStatus[]): boolean {
  return overdueDays(invoice.dueDate, invoice.status, statuses) !== null;
}

/**
 * HOW LATE an invoice is, in whole days — or `null` for "not overdue", which
 * covers every unknown as well.
 *
 * ⚠️ **This is the one definition of overdue, and `isOverdue()` is a thin call
 * to it.** They were separate for about ten minutes while this was written, and
 * that is already long enough for the interesting failure: a row can then show
 * an Overdue badge with no day count beside it, or a day count with no badge,
 * and a reader resolves the contradiction by trusting whichever they notice
 * first. Everything on `isOverdue()` about the asymmetry — an unknown is
 * UNSETTLED, so the badge shows, because noise is visible and gets corrected
 * where silence is not — is enforced here and applies unchanged.
 *
 * It takes the DATE and the STATUS rather than an `Invoice`, because the
 * University Partnerships dashboard holds neither — its rows are a projection
 * carrying `{id, name}` for the status and the bare `dueDate` string. Handing it
 * the two fields is what lets that page reuse this judgement instead of growing
 * a second one, and `isOverdue()` above still takes the whole record so no
 * existing call site changes.
 *
 * @param dueDate  Plain `YYYY-MM-DD`, or `null` where the invoice carries none.
 * @param status   The invoice's own `{id, name}` — no flag on it; see
 *                 {@link isStatusSettled}.
 * @param statuses The `/invoice-statuses` master, so the settled test is the
 *                 SERVER's answer rather than a vocabulary guess. Pass it
 *                 wherever the caller already holds it.
 */
export function overdueDays(
  dueDate: string | null,
  status: MasterRef | null,
  statuses?: readonly InvoiceStatus[],
): number | null {
  if (dueDate === null) return null;
  /*
   * `status: null` is a real state — convert resolves the Draft row BY NAME and
   * leaves the status null when no such row exists — and `isStatusSettled`
   * answers `false` for it deliberately, which is the safe direction.
   */
  if (isStatusSettled(status, statuses)) return null;

  const today = todayIsoDate();
  // The string comparison is the whole overdue test, exactly as before: ISO
  // dates sort lexicographically. The day count below only says HOW late.
  if (!(dueDate < today)) return null;

  const from = calendarDayNumber(dueDate);
  const to = calendarDayNumber(today);
  /*
   * ⚠️ **A count we cannot compute returns `null`, which reads as NOT OVERDUE —
   * and that is the one place this function fails in the reassuring direction,
   * so it is stated rather than buried.** It is reachable only on a `dueDate`
   * the server did not send as `YYYY-MM-DD`, which the contract says cannot
   * happen. The alternative — reporting "overdue by NaN days", or an unlabelled
   * badge with no figure — puts a broken-looking cell on the surface finance
   * reads, and this row is still on `/invoices`, where `isOverdue()` runs the
   * same test.
   */
  if (from === null || to === null) return null;
  return to - from;
}

/**
 * A `YYYY-MM-DD` string as a count of days, so two of them can be subtracted.
 *
 * ⚠️ **`Date.UTC` on the PARTS, never `new Date(string)`.** Parsing the string
 * gives midnight UTC and renders as the previous day west of Greenwich — the
 * bug `todayIsoDate()` above exists to avoid, mirrored. Both ends are placed on
 * the same UTC axis here purely as arithmetic; neither is ever converted back to
 * a local date, so no time zone and no DST transition can move the difference.
 *
 * `null` for anything that is not three numeric parts. See the caller for why
 * that is deliberately not distinguishable from "not overdue".
 */
function calendarDayNumber(isoDate: string): number | null {
  const parts = isoDate.split('-');
  if (parts.length !== 3) return null;
  const [year, month, day] = parts.map(Number);
  if (year === undefined || month === undefined || day === undefined) return null;
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null;
  return Math.round(Date.UTC(year, month - 1, day) / 86_400_000);
}
